// useWakeLock — hold a screen Wake Lock while `active` is true.
//
// iOS / Android suspend the page when the screen locks; a resumable
// upload then stalls until the user comes back. The Screen Wake Lock
// API keeps the display on while we transfer. The lock is released by
// the OS whenever the page is hidden, so we re-request it when the page
// becomes visible again while still active. Browsers without the API
// (older Safari) get a silent no-op — `held` stays false and the UI can
// tell the user to keep the screen on manually.

import { useEffect, useRef, useState } from 'react';

interface WakeLockSentinelLike {
  release: () => Promise<void>;
  addEventListener: (type: 'release', cb: () => void) => void;
}
interface WakeLockLike {
  request: (type: 'screen') => Promise<WakeLockSentinelLike>;
}

function getWakeLock(): WakeLockLike | null {
  if (typeof navigator === 'undefined') return null;
  const wl = (navigator as Navigator & { wakeLock?: WakeLockLike }).wakeLock;
  return wl && typeof wl.request === 'function' ? wl : null;
}

export const WAKE_LOCK_SUPPORTED = getWakeLock() != null;

export function useWakeLock(active: boolean): { held: boolean; supported: boolean } {
  const [held, setHeld] = useState(false);
  const sentinelRef = useRef<WakeLockSentinelLike | null>(null);

  useEffect(() => {
    const wl = getWakeLock();
    if (!active || !wl) {
      setHeld(false);
      return undefined;
    }
    let cancelled = false;

    const request = async () => {
      if (cancelled || sentinelRef.current || document.visibilityState !== 'visible') return;
      try {
        const s = await wl.request('screen');
        if (cancelled) {
          void s.release().catch(() => {});
          return;
        }
        sentinelRef.current = s;
        setHeld(true);
        s.addEventListener('release', () => {
          sentinelRef.current = null;
          setHeld(false);
        });
      } catch {
        // Denied (low battery, permissions policy) — silent fallback.
        setHeld(false);
      }
    };

    const onVisible = () => {
      if (document.visibilityState === 'visible') void request();
    };
    void request();
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onVisible);
      const s = sentinelRef.current;
      sentinelRef.current = null;
      if (s) void s.release().catch(() => {});
      setHeld(false);
    };
  }, [active]);

  return { held, supported: WAKE_LOCK_SUPPORTED };
}
